import { Sparkles, Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardHeader, Pill } from '../_primitives';

export type SuggestionKind = 'tone' | 'fact' | 'clarity' | 'seo';

export interface RailSuggestion {
  id: string;
  kind: SuggestionKind;
  /** Original text snippet (shown struck-through). */
  text: string;
  /** Proposed replacement. */
  fix: string;
  /** Optional explanation shown beneath. */
  reason?: string;
}

export interface SuggestionsCardProps {
  suggestions: RailSuggestion[];
  /** Whether suggestion underlines are currently visible in the doc. */
  enabled: boolean;
  onToggle: (next: boolean) => void;
  onApply?: (id: string) => void;
  onDismiss?: (id: string) => void;
  onGenerate?: () => void;
  generating?: boolean;
}

const KIND_TONE: Record<SuggestionKind, 'warning' | 'danger' | 'coral' | 'success'> = {
  tone: 'coral',
  fact: 'danger',
  clarity: 'warning',
  seo: 'success',
};

const KIND_LABEL: Record<SuggestionKind, string> = {
  tone: 'Tone',
  fact: 'Fact-check',
  clarity: 'Clarity',
  seo: 'SEO',
};

export function SuggestionsCard({
  suggestions,
  enabled,
  onToggle,
  onApply,
  onDismiss,
  onGenerate,
  generating = false,
}: SuggestionsCardProps) {
  return (
    <Card railSection="suggestions">
      <CardHeader
        icon={<Sparkles className="size-3.5" />}
        title="Inline suggestions"
        trailing={
          <button
            type="button"
            onClick={() => onToggle(!enabled)}
            aria-pressed={enabled}
            className={cn(
              'inline-flex items-center gap-2 text-[11px] font-semibold cursor-pointer rounded-full border transition-colors px-1 py-0.5',
              enabled
                ? 'bg-teal-light text-teal-dark border-teal/30'
                : 'bg-warm-bg text-muted-foreground border-border',
            )}
          >
            <span
              className={cn(
                'inline-block size-3.5 rounded-full transition-transform',
                enabled ? 'bg-teal translate-x-0' : 'bg-muted-foreground',
              )}
            />
            <span className="pr-1.5">{enabled ? 'On' : 'Off'}</span>
          </button>
        }
      />

      {suggestions.length === 0 ? (
        <div className="px-3 pb-4 text-center text-[12px] text-muted-foreground">
          <div className="mb-2">No generated suggestions yet.</div>
          {onGenerate && (
            <button
              type="button"
              onClick={onGenerate}
              disabled={generating}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-[11.5px] font-semibold cursor-pointer',
                'bg-card text-foreground border-border hover:bg-warm-bg',
                'disabled:opacity-60 disabled:cursor-not-allowed',
                !generating && 'animate-ai-suggest',
              )}
            >
              <Sparkles className={cn('size-3', generating && 'animate-spin')} />
              {generating ? 'Generating…' : 'Generate suggestions'}
            </button>
          )}
        </div>
      ) : (
        <div className="px-3 pb-3 flex flex-col gap-2">
          {suggestions.map((s) => (
            <div
              key={s.id}
              className="rounded-md border border-border bg-warm-bg/50 p-2.5"
            >
              <div className="flex items-center gap-2 mb-1.5">
                <Pill tone={KIND_TONE[s.kind]} size="sm">
                  {KIND_LABEL[s.kind]}
                </Pill>
                <div className="flex-1" />
              </div>
              <div className="text-[12px] leading-snug text-foreground mb-1">
                <span className="line-through text-muted-foreground">{s.text}</span>{' '}
                <span className="font-semibold text-teal-dark">{s.fix}</span>
              </div>
              {s.reason && (
                <div className="text-[11px] text-muted-foreground leading-snug mb-2">
                  {s.reason}
                </div>
              )}
              <div className="flex gap-1.5">
                <button
                  type="button"
                  onClick={() => onApply?.(s.id)}
                  className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1 rounded-md text-[11.5px] font-semibold bg-coral text-white border border-coral hover:bg-coral-hover cursor-pointer"
                >
                  <Check className="size-3" />
                  Apply
                </button>
                <button
                  type="button"
                  onClick={() => onDismiss?.(s.id)}
                  className="inline-flex items-center justify-center px-2 py-1 rounded-md text-[11.5px] font-medium bg-card text-muted-foreground border border-border hover:text-foreground cursor-pointer"
                >
                  <X className="size-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
